'use client';

import {
    type CustomerData,
    addItemAtom,
    availableShippingMethodsAtom,
    cartItemsAtom,
    cartNumberAtom,
    clearCartAtom,
    customerDataAtom,
    errorAtom,
    initializeCartAtom,
    isInitializedAtom,
    isLoadingAtom,
    removeItemAtom,
    selectShippingMethodAtom,
    selectedShippingMethodAtom,
    shippingCostAtom,
    subtotalAtom,
    totalAtom,
    updateCustomerAtom,
} from '@/lib/atoms/cart-atoms';
import { store } from '@/lib/store';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type ReactNode, useEffect } from 'react';

/**
 * JotaiCartProvider initializes cart state using Jotai atoms
 * This component should be placed high in the component tree
 */
export function JotaiCartProvider({ children }: { children: ReactNode }) {
    // Get the initialization atom
    const initialize = useSetAtom(initializeCartAtom, { store });
    const isInitialized = useAtomValue(isInitializedAtom, { store });

    // Initialize cart on mount
    useEffect(() => {
        if (!isInitialized) {
            initialize();
        }
    }, [initialize, isInitialized]);

    return <>{children}</>;
}

/**
 * Hook to access cart state and actions
 * Can be used anywhere in the application after JotaiCartProvider is mounted
 */
export function useJotaiCart() {
    // State atoms
    const [items] = useAtom(cartItemsAtom, { store });
    const [cartNumber] = useAtom(cartNumberAtom, { store });
    const [customerData] = useAtom(customerDataAtom, { store });
    const [availableShippingMethods] = useAtom(availableShippingMethodsAtom, { store });
    const [selectedShippingMethod] = useAtom(selectedShippingMethodAtom, { store });
    const [shippingCost] = useAtom(shippingCostAtom, { store });
    const [subtotal] = useAtom(subtotalAtom, { store });
    const total = useAtomValue(totalAtom, { store });
    const [isInitialized] = useAtom(isInitializedAtom, { store });
    const [isLoading] = useAtom(isLoadingAtom, { store });
    const [error] = useAtom(errorAtom, { store });

    // Action atoms
    const addItem = useSetAtom(addItemAtom, { store });
    const removeItem = useSetAtom(removeItemAtom, { store });
    const updateCustomer = useSetAtom(updateCustomerAtom, { store });
    const selectShippingMethod = useSetAtom(selectShippingMethodAtom, { store });
    const clearCart = useSetAtom(clearCartAtom, { store });

    // Wrap the action atoms with the expected function signatures
    const addItemToCart = async (purchasableId: number, quantity: number) => {
        return addItem({ purchasableId, quantity });
    };

    const removeItemFromCart = async (purchasableId: number, quantity?: number) => {
        return removeItem({ purchasableId, quantity });
    };

    const updateCustomerData = async (data: CustomerData) => {
        return updateCustomer(data);
    };

    const selectShippingMethodAction = async (methodName: string) => {
        return selectShippingMethod(methodName);
    };

    const clearCartAction = () => {
        clearCart();
    };

    return {
        items,
        total,
        subtotal,
        shippingCost,
        cartNumber,
        customerData,
        availableShippingMethods,
        selectedShippingMethod,
        addItem: addItemToCart,
        removeItem: removeItemFromCart,
        updateCustomer: updateCustomerData,
        selectShippingMethod: selectShippingMethodAction,
        clearCart: clearCartAction,
        isInitialized,
        isLoading,
        error,
    };
}
